import { Link } from "@tanstack/react-router";
import { getRelated, KIND_LABEL, KIND_NOUN, type ItemKind, type EcosystemItem } from "@/lib/ecosystem";

interface Props {
  kind: ItemKind;
  slug: string;
  limit?: number;
  heading?: string;
  className?: string;
}

/**
 * Cross-content "keep going" rail. Renders up to `limit` related items
 * pulled from the ecosystem graph (music / wisdom / activations / books).
 * Renders nothing when the graph has no matches — never shows an empty shell.
 */
export function RelatedRail({ kind, slug, limit = 4, heading = "Continue the transmission", className = "" }: Props) {
  const items = getRelated(kind, slug, limit);
  if (items.length === 0) return null;

  return (
    <section className={`mt-16 border-t hairline pt-12 ${className}`} aria-label="Related content">
      <div className="mb-8 flex items-baseline justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Ecosystem</p>
          <h2 className="mt-2 font-serif text-3xl">{heading}</h2>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <RelatedCard key={`${item.kind}:${item.slug}`} item={item} />
        ))}
      </div>
    </section>
  );
}

function RelatedCard({ item }: { item: EcosystemItem }) {
  const params = { slug: item.slug };
  return (
    <Link
      to={item.hrefRoute}
      params={params}
      className="group flex h-full flex-col rounded-xl border hairline bg-charcoal p-5 transition-all hover:border-gold hover:shadow-gold"
    >
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
        {KIND_LABEL[item.kind]} · {KIND_NOUN[item.kind]}
      </p>
      <h3 className="mt-3 font-serif text-lg leading-tight group-hover:text-gold-luminous">{item.title}</h3>
      <p className="mt-2 text-xs text-muted-foreground">{item.subtitle}</p>
      <span className="mt-4 inline-flex text-xs text-gold">Enter →</span>
    </Link>
  );
}
